import asyncSeries from 'async/series';
import React from 'react';
import EventEmitter from 'events';
import Stripe from '@stripe/stripe-js';

import UserStore from './user_store';

import util from '../tools/util';

export default {
  addChangeListener,
  init,
  loadStripe,
  getList,
  useList,
  fetch,
  createPaymentMethod,
  updateDefault,
  deleteMethod,
};

type StripeType = Stripe.Stripe;

const PAYMENT_ADD_TIMEOUT = 30 * 1000;

const STRIPE_KEY =
  process.env.NODE_ENV === 'development'
    ? process.env.STRIPE_PK
    : process.env.STRIPE_PK;

const g_orgListMap = new Map<number, any[]>();
let g_stripe: StripeType | undefined;
let g_stripePromise: any;

const g_eventEmitter = new EventEmitter();
const CHANGE_EVENT = 'change';
function addChangeListener(callback) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
}
function _removeChangeListener(callback) {
  g_eventEmitter.removeListener(CHANGE_EVENT, callback);
}
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function _subscribe(callback: any) {
  addChangeListener(callback);
  return () => {
    _removeChangeListener(callback);
  };
}
function getList(org_id: number) {
  return g_orgListMap.get(org_id);
}
export function useList(org_id: number) {
  const _get = React.useCallback(() => getList(org_id), [org_id]);
  return React.useSyncExternalStore(_subscribe, _get);
}
function init(done?: (err?: any) => void) {
  done?.();
}
export function loadStripe() {
  if (!g_stripePromise) {
    g_stripePromise = new Promise((resolve, reject) => {
      _load((err, stripe) => {
        if (err) {
          reject(err);
        } else {
          resolve(g_stripe);
        }
      });
    });
  }
  return g_stripePromise;
}
function _load(done) {
  let stripe_module;
  if (g_stripe) {
    done();
  } else {
    asyncSeries(
      [
        (done) => {
          import('@stripe/stripe-js').then((new_module) => {
            stripe_module = new_module;
            done();
          }, done);
        },
        (done) => {
          stripe_module.loadStripe(STRIPE_KEY).then((new_stripe) => {
            g_stripe = new_stripe;
            done();
          }, done);
        },
      ],
      (err) => done(err)
    );
  }
}
function fetch(org_id: number, done?: (err?: any) => void) {
  const opts = {
    url: `/api/1/org/${org_id}/payment_method`,
  };
  UserStore.get(opts, (err, body) => {
    if (err) {
      util.errorLog('payment_method.fetch: err:', err);
    } else if (body) {
      const old = g_orgListMap.get(org_id);
      if (!util.deepEqual(old, body.payment_method_list)) {
        g_orgListMap.set(org_id, body.payment_method_list);
        _emit('list');
      }
    }
    done?.(err);
  });
}
function createPaymentMethod(
  params,
  done: (err?: any, payment_method_id?: any) => void
) {
  const { org_id, card } = params;

  let stripe: StripeType;
  let stripe_payment_method_id;
  let payment_method_id;
  asyncSeries(
    [
      (done) => {
        if (g_stripe) {
          stripe = g_stripe;
          done();
        } else {
          done('not_initialized');
        }
      },
      (done) => {
        stripe.createPaymentMethod({ type: 'card', card }).then(
          (payload) => {
            let err;
            if (payload.error) {
              err = payload.error;
              util.errorLog(
                'createPaymentMethod: payload err:',
                payload.error,
                payload
              );
            } else {
              stripe_payment_method_id = payload.paymentMethod.id;
            }
            done(err);
          },
          (err) => {
            util.errorLog('createPaymentMethod: create err:', err);
            done(err);
          }
        );
      },
      (done) => {
        const opts = {
          url: `/api/1/org/${org_id}/payment_method`,
          body: { stripe_payment_method_id },
          timeout: PAYMENT_ADD_TIMEOUT,
        };
        UserStore.post(opts, (err, body) => {
          if (err) {
            util.errorLog('createPaymentMethod: create err:', err);
          } else {
            payment_method_id = body?.payment_method_id;
          }
          done(err);
        });
      },
      (done) =>
        fetch(org_id, (err) => {
          if (err) {
            util.errorLog('createPaymentMethod.fetch: err:', err);
          }
          done();
        }),
    ],
    (err) => done(err, payment_method_id)
  );
}
function updateDefault(params, done) {
  const { org_id, payment_method_id } = params;
  const opts = {
    url: `/api/1/org/${org_id}/payment_method/${payment_method_id}/default`,
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('updateDefault: update err:', err);
      done(err);
    } else {
      fetch(org_id, () => done());
    }
  });
}
function deleteMethod(params, done) {
  const { org_id, payment_method_id } = params;
  const opts = {
    url: `/api/1/org/${org_id}/payment_method/${payment_method_id}`,
  };
  UserStore.del(opts, (err, body) => {
    if (err) {
      util.errorLog('deleteMethod: delete err:', err);
      done(err);
    } else {
      fetch(org_id, () => done());
    }
  });
}
